/** Default client-side sort for Course Analysis question diagnostics. */
export const COURSE_ANALYSIS_LIST_DEFAULTS = {
  sortBy: 'question',
  sortDir: 'asc',
};

/** Sort keys for the question diagnostics table. */
export const COURSE_ANALYSIS_SORT_FIELDS = {
  question: 'question',
  difficulty: 'difficulty',
  accuracy: 'accuracy',
  discrimination: 'discrimination',
  note: 'note',
};

/**
 * Computes the next client-side sort when a column header is clicked.
 * Same column toggles asc/desc; a new column always starts asc.
 */
export function getNextCourseAnalysisSort(columnKey, currentSortBy, currentSortDir) {
  const sortBy = columnKey || COURSE_ANALYSIS_LIST_DEFAULTS.sortBy;
  if (currentSortBy === sortBy) {
    return {
      sortBy,
      sortDir: currentSortDir === 'asc' ? 'desc' : 'asc',
    };
  }
  return { sortBy, sortDir: 'asc' };
}

/** Resolves question text for display and sorting. */
function questionText(row) {
  return row.q || row.text || '';
}

/** Maps discrimination labels to a numeric rank for stable sorting. */
function discriminationRank(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (normalized === 'high') return 3;
  if (normalized === 'med' || normalized === 'medium') return 2;
  if (normalized === 'low') return 1;
  return 0;
}

/**
 * Resolves a row field value for client-side column sorting.
 * Numeric columns return numbers; everything else returns strings.
 */
function courseAnalysisSortValue(row, sortBy) {
  switch (sortBy) {
    case COURSE_ANALYSIS_SORT_FIELDS.difficulty:
      return row.diff || row.difficulty || '';
    case COURSE_ANALYSIS_SORT_FIELDS.accuracy:
      return Number(row.acc ?? row.accuracy ?? 0);
    case COURSE_ANALYSIS_SORT_FIELDS.discrimination:
      return discriminationRank(row.disc || row.discrimination);
    case COURSE_ANALYSIS_SORT_FIELDS.note:
      return row.flag || '';
    case COURSE_ANALYSIS_SORT_FIELDS.question:
    default:
      return questionText(row);
  }
}

/**
 * Sorts flagged question rows in memory — no API round-trip.
 */
export function sortCourseAnalysisLocally(rows, sortBy, sortDir) {
  const list = Array.isArray(rows) ? [...rows] : [];
  const dir = sortDir === 'desc' ? -1 : 1;
  const field = sortBy || COURSE_ANALYSIS_LIST_DEFAULTS.sortBy;

  list.sort((a, b) => {
    const av = courseAnalysisSortValue(a, field);
    const bv = courseAnalysisSortValue(b, field);
    if (typeof av === 'number' && typeof bv === 'number') {
      return (av - bv) * dir;
    }
    return String(av).localeCompare(String(bv), undefined, { sensitivity: 'base' }) * dir;
  });

  return list;
}
